import mainStyle from '../mainStyle.module.scss';

import { useState, useEffect } from 'react';

import { cloneDeep } from 'lodash';

import { Checkbox, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ReactComponent as CheckBoxDefault } from 'assets/images/icons/checkbox_default.svg';
import { ReactComponent as CheckClearIcon } from 'assets/images/icons/checkbox_clear.svg';
import { ReactComponent as CheckBoxFilled } from 'assets/images/icons/checkbox_fiilled.svg';

import MacAddressInputField from 'components/InputField/MacAddressInputField';

const validateMacAddress = macAddress => {
  const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
  return macRegex.test(macAddress);
};

const OuiBindingTable = ({ formData, setFormData, hasChecked, setHasChecked, setIsSelectedCount, showEditGeneral }) => {
  const theme = useTheme();

  const [allChecked, setAllChecked] = useState(false);

  const checkInputInvalid = (value, field) => {
    if (value === '') return false; // Empty value is not considered invalid
    const isInvalidMac = validateMacAddress(value) === false;
    const isDuplicate = field !== 'mask' && formData?.ouiBindingList?.filter(item => item[field] === value).length > 1;
    return isInvalidMac || isDuplicate;
  };

  const handleChangeItemValue = (index, field, value) => {
    const newList = cloneDeep(formData.ouiBindingList);
    newList[index][field] = value;
    setFormData(prev => ({
      ...prev,
      ouiBindingList: newList,
    }));
  };

  const handleChangeCheckbox = (key, isChecked, index) => {
    if (key === 'CHECK_ALL') {
      setFormData(prev => {
        const list = prev?.ouiBindingList || [];
        const newList = list.map(item => ({ ...item, checked: !!isChecked }));
        setAllChecked(!!isChecked);
        setHasChecked(newList.length > 0 && newList.some(r => r.checked));
        setIsSelectedCount(isChecked ? newList.length : 0);
        return { ...prev, ouiBindingList: newList };
      });
    } else {
      setFormData(prev => {
        const newList = (prev.ouiBindingList || []).map((item, idx) => (idx === index ? { ...item, checked: !!isChecked } : item));
        setAllChecked(newList.length > 0 && newList.every(r => r.checked));
        setHasChecked(newList.some(r => r.checked));
        setIsSelectedCount(newList.filter(i => i.checked).length || 0);
        return { ...prev, ouiBindingList: newList };
      });
    }
  };

  useEffect(() => {
    if (formData?.ouiBindingList?.length > 0) {
      setAllChecked(formData.ouiBindingList.every(row => row.checked));
      setHasChecked(formData.ouiBindingList.some(row => row.checked));
      setIsSelectedCount(formData.ouiBindingList.filter(item => item.checked)?.length || 0);
    } else {
      setAllChecked(false);
      setHasChecked(false);
      setIsSelectedCount(0);
    }
  }, [formData?.ouiBindingList]);

  return (
    <TableContainer>
      <Table>
        <TableHead
          sx={{
            '& th': {
              color: '#aaaaaa',
              fontSize: '15px',
              fontWeight: '500',
            },
            borderTop: 'none',
            borderColor: '1px solid #d5d5d5',
          }}
        >
          <TableRow sx={{ backgroundColor: '#ffffff' }}>
            {showEditGeneral && (
              <TableCell
                sx={{
                  width: '56px',
                  padding: '4px 8px',
                  '&:not(:last-of-type)::after': {
                    display: 'none',
                  },
                }}
              >
                <Checkbox
                  icon={hasChecked ? <CheckClearIcon style={{ width: '24px', height: '24px' }} /> : <CheckBoxDefault style={{ width: '24px', height: '24px' }} />}
                  checkedIcon={<CheckBoxFilled style={{ width: '24px', height: '24px' }} />}
                  checked={!!allChecked}
                  onChange={e => handleChangeCheckbox('CHECK_ALL', e.target.checked, null)}
                  sx={{
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                />
              </TableCell>
            )}
            <TableCell>
              <Typography className={mainStyle['table-header']}>OUI Address</Typography>
            </TableCell>
            <TableCell>
              <Typography className={mainStyle['table-header']}>Mask</Typography>
            </TableCell>
            <TableCell>
              <Typography className={mainStyle['table-header']}>Description (Optional)</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {formData?.ouiBindingList?.length > 0 ? (
            formData.ouiBindingList.map((item, index) => (
              <TableRow
                key={`oui-binding-table-row-${index}`}
                sx={{
                  backgroundColor: index % 2 === 1 ? theme.palette.primary.whiteSecound : theme.palette.primary.white,
                  '& .MuiTableCell-root': { border: 'none', padding: '6px 8px' },
                }}
              >
                {showEditGeneral && (
                  <TableCell sx={{ width: '56px', padding: '4px 8px' }}>
                    <Checkbox
                      icon={<CheckBoxDefault style={{ width: '24px', height: '24px' }} />}
                      checkedIcon={<CheckBoxFilled style={{ width: '24px', height: '24px' }} />}
                      checked={!!item.checked}
                      onClick={e => e.stopPropagation()}
                      onChange={e => handleChangeCheckbox('NOT_CHECK_ALL', e.target.checked, index)}
                      sx={{
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <MacAddressInputField
                    value={item.address}
                    onChange={newVal => handleChangeItemValue(index, 'address', newVal)}
                    disabled={!showEditGeneral}
                    showError={checkInputInvalid(item.address, 'address')}
                  />
                </TableCell>
                <TableCell>
                  <MacAddressInputField
                    value={item.mask}
                    onChange={newVal => handleChangeItemValue(index, 'mask', newVal)}
                    disabled={!showEditGeneral}
                    showError={checkInputInvalid(item.mask, 'mask')}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        height: '32px',
                        width: '260px',
                      },
                    }}
                    value={item.description}
                    onChange={e => {
                      handleChangeItemValue(index, 'description', e.target.value);
                    }}
                    disabled={!showEditGeneral}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow
              sx={{
                '& .MuiTableCell-root': { border: 'none' },
              }}
            >
              <TableCell colSpan={4} align="center">
                <Typography
                  sx={{
                    fontSize: theme.typography.customFontSize[2],
                    color: theme.palette.primary.black,
                  }}
                >
                  No entry in the table
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OuiBindingTable;
